import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import BoltIcon from '@mui/icons-material/Bolt';
import SettingsIcon from '@mui/icons-material/Settings';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneIcon from '@mui/icons-material/Tune';
import { addDoc, collection, query, serverTimestamp, where, updateDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase';
import LiveHomepageThumbnail from './LiveHomepageThumbnail';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';

const HEALTH_WINDOW_MS = 10 * 60 * 1000;

function normalizeUrl(raw) {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

function parseDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function healthChip(app) {
  const hb = parseDate(app.heartbeatAt);
  const liveStatus = app.liveStatus || 'unknown';
  if (liveStatus === 'down') return { label: 'DOWN', color: 'error' };
  if (liveStatus === 'degraded') return { label: 'DEGRADED', color: 'warning' };
  if (liveStatus === 'live') {
    if (!hb) return { label: 'LIVE', color: 'success' };
    return Date.now() - hb.getTime() <= HEALTH_WINDOW_MS ?
      { label: 'LIVE', color: 'success' } :
      { label: 'STALE', color: 'warning' };
  }
  return { label: 'UNKNOWN', color: 'default' };
}

export default function CompanyAppsStatusBoard({
  userData,
  compact = false,
  title = 'Uptime Monitoring',
  showHeader = true,
  configureBasePath = '/site-app',
}) {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const companyId = userData?.companyId;
  const role = userData?.role;
  const canManageApps =
    role === 'company_admin' || role === 'developer' || role === 'super_admin';
  const [newName, setNewName] = useState('');
  const [newHomepageUrl, setNewHomepageUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [settingsMenu, setSettingsMenu] = useState(null);

  const appsQuery = useMemo(() => {
    if (!companyId) return null;
    return query(collection(db, 'companyApps'), where('companyId', '==', companyId));
  }, [companyId]);

  const {
    data: appsRaw,
    loading,
    error: queryError,
  } = useFirestoreQuery(appsQuery, { enabled: Boolean(companyId) });

  const apps = useMemo(() => {
    const rows = [...appsRaw];
    rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return rows;
  }, [appsRaw]);

  const error = actionError || queryError?.message || '';

  const liveCount = useMemo(
    () => apps.filter((a) => healthChip(a).label === 'LIVE').length,
    [apps],
  );

  const handleAdd = async () => {
    const name = newName.trim();
    const homepageUrl = normalizeUrl(newHomepageUrl);
    if (!name || !homepageUrl || !companyId) return;
    setSaving(true);
    setActionError('');
    try {
      const cur = auth.currentUser?.uid;
      await addDoc(collection(db, 'companyApps'), {
        companyId,
        name,
        homepageUrl,
        previewImageUrl: '',
        stewardUserIds: cur ? [cur] : [],
        liveStatus: 'live',
        heartbeatAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        createdBy: cur || null,
        livenessAlertsEnabled: true,
      });
      setNewName('');
      setNewHomepageUrl('');
    } catch (e) {
      console.error('add app:', e);
      setActionError(e.message || 'Could not add app.');
    } finally {
      setSaving(false);
    }
  };

  const markLiveHeartbeat = async (appId) => {
    try {
      const runProbe = httpsCallable(functions, 'runCompanyAppLivenessNow');
      await runProbe({ appId });
    } catch (e) {
      console.error('markLiveHeartbeat:', e);
      setActionError(e.message || 'Could not run liveness check.');
    }
  };

  const handleToggleAppLivenessAlerts = async (app, enabled) => {
    if (!app?.id) return;
    setActionError('');
    try {
      await updateDoc(doc(db, 'companyApps', app.id), { livenessAlertsEnabled: enabled });
      setSettingsMenu((m) => (m?.app?.id === app.id ? { ...m, app: { ...m.app, livenessAlertsEnabled: enabled } } : m));
    } catch (e) {
      console.error('toggle app liveness alerts:', e);
      setActionError(e.message || 'Could not update alert setting.');
    }
  };

  const uid = auth.currentUser?.uid || null;
  const canRemoveApp = (app) => {
    if (!uid) return false;
    if (role === 'super_admin') return true;
    if (role === 'company_admin') {
      return !app.createdBy || app.createdBy === uid;
    }
    if (role === 'developer') {
      return app.createdBy === uid;
    }
    return false;
  };

  const confirmDeleteApp = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    setActionError('');
    try {
      const del = httpsCallable(functions, 'deleteCompanyApp');
      await del({ appId: deleteTarget.id });
      setDeleteTarget(null);
    } catch (e) {
      console.error('deleteCompanyApp:', e);
      setActionError(e.message || 'Could not remove app.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {showHeader && (
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant={compact ? 'h6' : 'h4'} gutterBottom={!compact}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Homepage preview (live capture) and liveness snapshot for your company apps.
            </Typography>
          </Box>
          <Chip
            icon={<BoltIcon />}
            label={`${liveCount}/${apps.length} live`}
            color={liveCount === apps.length && apps.length > 0 ? 'success' : 'default'}
            variant="outlined"
          />
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{error}</Alert>}
      {!loading && apps.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No apps configured yet.
        </Alert>
      )}

      {canManageApps && !compact && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Add app / website monitor</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Cards show a live-style capture of each homepage when the site allows it. Optional image override is available in Configuration.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField size="small" label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} fullWidth />
              <TextField size="small" label="Homepage URL" value={newHomepageUrl} onChange={(e) => setNewHomepageUrl(e.target.value)} fullWidth />
              <Button variant="contained" onClick={handleAdd} disabled={saving || !newName.trim() || !newHomepageUrl.trim()}>
                Add
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {apps.map((app) => {
          const hc = healthChip(app);
          const hb = parseDate(app.heartbeatAt);
          return (
            <Grid size={{ xs: 12, md: compact ? 6 : 4 }} key={app.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" noWrap>{app.name || 'Untitled app'}</Typography>
                      <Link href={app.homepageUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        Visit
                        <OpenInNewIcon fontSize="inherit" />
                      </Link>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.25} flexShrink={0}>
                      <Chip icon={<PublicIcon />} size="small" label={hc.label} color={hc.color} />
                      {canManageApps && (
                        <IconButton
                          size="small"
                          aria-label={`Options for ${app.name || 'app'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSettingsMenu({ anchorEl: e.currentTarget, app });
                          }}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      mt: 1.5,
                      borderRadius: 1,
                      height: compact ? 120 : 150,
                      overflow: 'hidden',
                    }}
                  >
                    <LiveHomepageThumbnail
                      homepageUrl={app.homepageUrl}
                      customImageUrl={app.previewImageUrl}
                      height={compact ? 120 : 150}
                      compact={compact}
                    />
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    Last heartbeat: {hb ? hb.toLocaleString() : 'Never'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Menu
        anchorEl={settingsMenu?.anchorEl}
        open={Boolean(settingsMenu)}
        onClose={() => setSettingsMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => {
            const targetApp = settingsMenu?.app;
            setSettingsMenu(null);
            if (targetApp) {
              const base = configureBasePath.replace(/\/$/, '');
              navigate(`${base}/${targetApp.id}/configure`, {
                state: {
                  returnTo: `${routerLocation.pathname}${routerLocation.search}`,
                },
              });
            }
          }}
        >
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Configuration" secondary="Name, stewards, preview" />
        </MenuItem>
        <MenuItem
          onClick={(e) => e.stopPropagation()}
          disableRipple
          sx={{ cursor: 'default', py: 1 }}
        >
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={settingsMenu?.app ? settingsMenu.app.livenessAlertsEnabled !== false : true}
                onChange={(e) => {
                  e.stopPropagation();
                  const a = settingsMenu?.app;
                  if (a) handleToggleAppLivenessAlerts(a, e.target.checked);
                }}
              />
            )}
            label="Liveness inbox notifications"
            onClick={(e) => e.stopPropagation()}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const targetApp = settingsMenu?.app;
            setSettingsMenu(null);
            if (targetApp) markLiveHeartbeat(targetApp.id);
          }}
        >
          <ListItemIcon>
            <NetworkCheckIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Run liveness check" secondary="Probe homepage now" />
        </MenuItem>
        {settingsMenu?.app && canRemoveApp(settingsMenu.app) && (
          <MenuItem
            onClick={() => {
              const targetApp = settingsMenu.app;
              setSettingsMenu(null);
              setDeleteTarget(targetApp);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Remove monitor"
              secondary="Delete from uptime monitoring"
              primaryTypographyProps={{ color: 'error' }}
              secondaryTypographyProps={{ color: 'error', sx: { opacity: 0.85 } }}
            />
          </MenuItem>
        )}
      </Menu>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Remove app monitor?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes &quot;{deleteTarget?.name || 'this app'}&quot; from uptime monitoring. You can add it again later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteApp} disabled={deleting}>
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
